// ./src/components/features/UserInvitations/UserInvitationFormModal/UserInvitationFormModal.tsx
import React, { useState, useEffect } from "react";
import { TextInput, Stack, Text, Grid, Alert } from "@mantine/core";

import { UserInvitation, UserInvitationInput } from "@/utils/api";
import { AppModal, AppIcon } from "@/components/generic";
import { USER_ROLES } from "@/config/userRoles";
import { UserRoleSelector } from "../../UserRoleSelector";

export interface UserInvitationFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** User invitation to edit (if null/undefined, it's create mode) */
  userInvitation?: UserInvitation | null;

  /** Callback when user invitation should be created */
  onCreateInvitation?: (
    invitationData: UserInvitationInput
  ) => Promise<UserInvitation | null>;

  /** Callback when user invitation should be updated */
  onUpdateInvitation?: (
    invitationId: string,
    invitationData: UserInvitationInput
  ) => Promise<UserInvitation | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from invitation operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface UserInvitationFormData {
  firstName: string;
  lastName: string;
  email: string;
  userRoleId: string | null;
}

const initialFormData: UserInvitationFormData = {
  firstName: "",
  lastName: "",
  email: "",
  userRoleId: USER_ROLES.MANAGER_ROLE,
};

export const UserInvitationFormModal: React.FC<
  UserInvitationFormModalProps
> = ({
  opened,
  onClose,
  userInvitation,
  onCreateInvitation,
  onUpdateInvitation,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<UserInvitationFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(userInvitation) && Boolean(userInvitation?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or invitation changes
  useEffect(() => {
    if (opened) {
      if (userInvitation) {
        // Edit mode - populate form with invitation data
        setFormData({
          firstName: userInvitation.firstName || "",
          lastName: userInvitation.lastName || "",
          email: userInvitation.email || "",
          userRoleId: userInvitation.userRoleId || USER_ROLES.MANAGER_ROLE,
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, userInvitation, onClearError]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof UserInvitationFormData,
    value: string | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof UserInvitationFormData
  ): string | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare invitation data
      const invitationData: UserInvitationInput = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        userRoleId: formData.userRoleId,
      };

      let result: UserInvitation | null = null;

      if (isEditMode && userInvitation && onUpdateInvitation) {
        // Update existing invitation
        result = await onUpdateInvitation(userInvitation.id!, invitationData);
      } else if (!isEditMode && onCreateInvitation) {
        // Create new invitation
        result = await onCreateInvitation(invitationData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit User Invitation" : "Invite New User"}
      size="md"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: isEditMode ? "Update" : "Send Invitation",
          variant: "primary",
          iconName: isEditMode ? "Check" : "Send",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              error={getDisplayError("firstName")}
              required
              withAsterisk
              size="md"
              disabled={isSubmitting}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              error={getDisplayError("lastName")}
              required
              withAsterisk
              size="md"
              disabled={isSubmitting}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              error={getDisplayError("email")}
              required
              withAsterisk
              size="md"
              disabled={isSubmitting}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={12}>
            <UserRoleSelector
              label="User role"
              placeholder="Select user role"
              value={formData.userRoleId}
              onChange={(value) => handleInputChange("userRoleId", value)}
              error={getDisplayError("userRoleId")}
              required
              disabled={isSubmitting}
            />
          </Grid.Col>
        </Grid>

        {!isEditMode && (
          <Alert
            color="blue"
            icon={<AppIcon iconName="InfoCircle" size={16} />}
          >
            <Text size="sm">
              An invitation email will be sent to the provided email address.
              The invitation will be valid for 7 days.
            </Text>
          </Alert>
        )}

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
